import { forwardRef, useContext, useState } from "react";
import {
  ChevronDownIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { LanguageContext } from "../../context/LanguageContext";

const SelectSimple = forwardRef(function SelectSimple(
  {
    label,
    name,
    options = [],
    value,
    onChange,
    error,
    success,
    required = false,
    disabled = false,
    className = "",
    placeholder,
    ...props
  },
  ref
) {
  const { lang } = useContext(LanguageContext);
  const isRTL = lang === "ar";
  const dir = isRTL ? "rtl" : "ltr";

  const [focused, setFocused] = useState(false);
  const hasValue = Boolean(value);

  return (
    <div className={`w-full space-y-1 ${className}`} dir={dir}>
      <div className="relative">
        {/* SELECT */}
        <select
          ref={ref}
          id={name}
          name={name}
          value={value}
          disabled={disabled}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`
            peer w-full h-12 px-4 pt-5 pr-10
            rounded-lg
            bg-white dark:bg-gray-900
            border
            text-sm
            text-gray-900 dark:text-gray-100
            appearance-none cursor-pointer
            transition
            focus:outline-none focus:ring-2
            disabled:opacity-50 disabled:cursor-not-allowed
            ${isRTL ? "text-right" : "text-left"}
            ${
              error
                ? "border-red-500 focus:ring-red-500/20"
                : success
                ? "border-green-500 focus:ring-green-500/20"
                : "border-gray-300 dark:border-gray-700 focus:border-blue-600 focus:ring-blue-600/20"
            }
          `}
          {...props}
        >
          {/* Placeholder option */}
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}

          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* FLOATING LABEL */}
        {label && (
          <label
            htmlFor={name}
            className={`
              absolute px-1 
              text-sm
              transition-all pointer-events-none
              ${
                focused || hasValue
                  ? "top-1 text-xs text-blue-600 dark:text-blue-400"
                  : "top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400"
              }
              ${isRTL ? "right-3" : "left-3"}
            `}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* CHEVRON */}
        <ChevronDownIcon
          className={`
            absolute top-1/2 -translate-y-1/2 w-5 h-5
            text-gray-400 pointer-events-none
            ${isRTL ? "left-4" : "right-4"}
          `}
        />

        {/* STATUS ICON */}
        {error && (
          <ExclamationCircleIcon
            className={`
              absolute top-1/2 -translate-y-1/2 w-5 h-5 text-red-500
              ${isRTL ? "left-10" : "right-10"}
            `}
          />
        )}

        {success && !error && (
          <CheckCircleIcon
            className={`
              absolute top-1/2 -translate-y-1/2 w-5 h-5 text-green-500
              ${isRTL ? "left-10" : "right-10"}
            `}
          />
        )}
      </div>

      {/* ERROR */}
      {error && (
        <p className={`text-xs text-red-600 dark:text-red-400 ${isRTL ? "text-right" : ""}`}>
          {error}
        </p>
      )}
    </div>
  );
});

export default SelectSimple;
